import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { businessIntelligenceAPI } from '../lib/api';
import {
  FiArchive, FiPlus, FiTrash2, FiLoader, FiAlertCircle,
  FiCheckCircle, FiXCircle, FiClock, FiRefreshCw, FiFileText,
  FiX, FiChevronDown, FiCalendar, FiStopCircle, FiSearch, FiFilter,
  FiCheckSquare,
} from 'react-icons/fi';

function defaultDateRange() {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = d => d.toISOString().slice(0, 10);
  return { start: fmt(firstOfMonth), end: fmt(lastOfMonth) };
}

function NewRunModal({ onClose, onCreated, defaultGroupId, defaultEntryStage }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(defaultGroupId || '');
  const defaults = defaultDateRange();
  const [periodStart, setPeriodStart] = useState(defaults.start);
  const [periodEnd, setPeriodEnd] = useState(defaults.end);
  const [references, setReferences] = useState('');
  const [entryStage, setEntryStage] = useState(defaultEntryStage || 'wip');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    businessIntelligenceAPI.getGroups()
      .then(res => {
        setGroups(res.data?.groups || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const selectedGroupObj = groups.find(g => g.id === selectedGroup);
  const rfqAvailable = !!selectedGroupObj?.rfq_enabled;

  useEffect(() => {
    if (!rfqAvailable && entryStage === 'rfq') setEntryStage('wip');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, rfqAvailable]);

  const handleCreate = async () => {
    if (!selectedGroup || !periodStart || !periodEnd) return;
    if (periodStart > periodEnd) {
      setError('From date must be before To date.');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const refs = references.trim().split(',').map(r => r.trim()).filter(Boolean);
      const res = await businessIntelligenceAPI.createSession({ group_id: selectedGroup, period_start: periodStart, period_end: periodEnd, references: refs, entry_stage: entryStage });
      onCreated(res.data.session);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-green-700">
            <FiArchive className="w-5 h-5" />
            <h2 className="text-base font-semibold">New Business Intelligence Run</h2>
          </div>
          <button onClick={onClose} disabled={creating} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Intelligence Group</label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <FiLoader className="w-4 h-4 animate-spin" />
                Loading groups...
              </div>
            ) : groups.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                No Business Intelligence groups configured. Set up groups in System Settings → Business Intelligence first.
              </p>
            ) : (
              <div className="relative">
                <select
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                  className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select a group...</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>
          {rfqAvailable && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEntryStage('rfq')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    entryStage === 'rfq' ? 'bg-amber-50 border-amber-400 text-amber-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  RFQ
                </button>
                <button
                  type="button"
                  onClick={() => setEntryStage('wip')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    entryStage === 'wip' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Work in Progress
                </button>
              </div>
            </div>
          )}
          {/* Date range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <FiCalendar className="w-3.5 h-3.5" />
              Date Range
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={e => setPeriodStart(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={e => setPeriodEnd(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Files and emails modified/received within this range will be synced.</p>
          </div>
          {/* References */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">References <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="text"
              value={references}
              onChange={e => setReferences(e.target.value)}
              placeholder="e.g. INV-2026, WHEY PERMEATE, imports"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Comma-separated references. Each entry is searched as-is — use spaces within an entry for multi-word searches (e.g. "WHEY PERMEATE, 12357P").</p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !selectedGroup || !periodStart || !periodEnd}
            className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {creating ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
            {entryStage === 'rfq' ? 'Start RFQ' : 'Start Business Intelligence Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ session, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <FiTrash2 className="w-5 h-5" />
            <h2 className="text-base font-semibold">Delete Business Intelligence Run</h2>
          </div>
          <button onClick={onCancel} disabled={deleting} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700">
            You are about to permanently delete <span className="font-semibold">"{session.name}"</span>.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 space-y-1">
            <p className="font-medium">This will permanently delete:</p>
            <ul className="list-disc list-inside space-y-0.5 text-red-700 mt-1">
              <li>The Business Intelligence session and all its settings</li>
              <li>All {session.doc_count || 0} synced document records</li>
              <li>All AI extraction results and tags</li>
            </ul>
            <p className="mt-2 font-medium">This action cannot be undone.</p>
          </div>
          <p className="text-xs text-gray-500">
            Original files in SharePoint, Exchange or Doxolve are not affected — only the archive data stored in Doxolve is deleted.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {deleting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_CONFIG = {
  pending:    { icon: FiClock,       color: 'text-gray-500',   bg: 'bg-gray-100',   label: 'Pending' },
  syncing:    { icon: FiRefreshCw,   color: 'text-blue-600',   bg: 'bg-blue-100',   label: 'Syncing',     spin: true },
  processing: { icon: FiLoader,      color: 'text-purple-600', bg: 'bg-purple-100', label: 'Processing',  spin: true },
  complete:   { icon: FiCheckCircle, color: 'text-green-600',  bg: 'bg-green-100',  label: 'Complete' },
  archived:   { icon: FiArchive,     color: 'text-gray-600',   bg: 'bg-gray-100',   label: 'Archived' },
  error:      { icon: FiXCircle,     color: 'text-red-600',    bg: 'bg-red-100',    label: 'Error' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

// RFQ is a display stage, not a pipeline status — a session tagged stage='rfq' still carries a
// normal status (Syncing/Processing/...) shown via StatusBadge; this is just an additional label.
function RfqTag() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <FiFileText className="w-3 h-3" />
      RFQ
    </span>
  );
}

function formatDateTime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function formatPeriod(start, end) {
  if (!start || !end) return null;
  const s = new Date(start + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  const e = new Date(end + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
}

// Sort order requested for the run list: Pending, then Syncing, then Processing (grouped with
// Syncing as "in progress" isn't distinguished further), then Complete, then Archived last —
// each group ordered by reference number descending (highest/newest ref first). A status not
// in this map (shouldn't happen, but defensive) sorts after everything else.
const STATUS_SORT_ORDER = { pending: 0, syncing: 1, processing: 2, complete: 3, archived: 4 };

// Pulls the trailing numeric portion off a reference like "PO2000512" -> 2000512, so refs sort
// by their actual number rather than as text (which would put "PO9" before "PO10").
function refSortValue(references) {
  const ref = Array.isArray(references) && references.length > 0 ? references[0] : '';
  const rfqMatch = String(ref).match(/^RFQ(\d{6})-(\d+)$/i);
  if (rfqMatch) {
    // YYMMDD * 100000 leaves room for the sequence number without the two ever colliding, and
    // keeps later dates sorting above earlier ones the same way the plain-number case does.
    return parseInt(rfqMatch[1], 10) * 100000 + parseInt(rfqMatch[2], 10);
  }
  const match = String(ref).match(/(\d+)\D*$/);
  return match ? parseInt(match[1], 10) : -Infinity;
}

// Pulls the Executive Summary section out of the session's AI Report markdown (always the
// first "## " section, generated right after the report's opening heading — see
// bi-ai-processor.service.js's report prompt) so it can be shown on the list card and searched
// without needing a dedicated summary column.
function extractExecutiveSummary(aiReport) {
  if (!aiReport) return '';
  const match = aiReport.match(/##\s*Executive Summary\s*\n([\s\S]*?)(?=\n##\s|$)/i);
  return match ? match[1].trim() : '';
}

const PAGE_SIZE = 20;

export default function BusinessIntelligence() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [stopping, setStopping] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // ── Filters ──
  // `group` and `view` (wip | archive) can arrive as query params from the sidebar's
  // per-group sub-navigation, e.g. /business-intelligence?group=Imports&view=wip
  const [searchText, setSearchText] = useState('');
  const [filterGroup, setFilterGroup] = useState(searchParams.get('group') || '');
  const [filterView, setFilterView] = useState(searchParams.get('view') || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Keep filters in sync if the sidebar link changes while already on this page
  useEffect(() => {
    const group = searchParams.get('group') || '';
    const view = searchParams.get('view') || '';
    setFilterGroup(group);
    setFilterView(view);
  }, [searchParams]);

  useEffect(() => {
    businessIntelligenceAPI.getGroups()
      .then(res => setGroups(res.data?.groups || []))
      .catch(() => {});
  }, []);

  const defaultGroupIdForModal = useMemo(() => {
    if (!filterGroup) return '';
    return groups.find(g => g.name === filterGroup)?.id || '';
  }, [filterGroup, groups]);

  const loadSessions = useCallback(async () => {
    try {
      setError(null);
      const res = await businessIntelligenceAPI.getSessions();
      setSessions(res.data.sessions || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    const inProgress = sessions.some(s => ['syncing', 'processing'].includes(s.status));
    if (!inProgress) return;
    const timer = setInterval(loadSessions, 5000);
    return () => clearInterval(timer);
  }, [sessions, loadSessions]);

  const handleCreated = (session) => {
    setShowModal(false);
    navigate(`/business-intelligence/${session.id}`);
  };

  const handleDeleteClick = (e, session) => {
    e.stopPropagation();
    setConfirmDelete(session);
  };

  const handleStop = async (e, session) => {
    e.stopPropagation();
    try {
      setStopping(session.id);
      await businessIntelligenceAPI.markSessionError(session.id, 'Stopped by user');
      await loadSessions();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setStopping(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(confirmDelete.id);
      await businessIntelligenceAPI.deleteSession(confirmDelete.id);
      setSessions(prev => prev.filter(s => s.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };

  const activeCount = sessions.filter(s => ['syncing', 'processing'].includes(s.status)).length;

  // Derive unique group names for the group filter dropdown
  const groupNames = useMemo(() => [...new Set(sessions.map(s => s.group_name).filter(Boolean))].sort(), [sessions]);

  const hasActiveFilters = searchText || filterGroup || filterView || filterStatus || filterDateFrom || filterDateTo;

  const filteredSessions = useMemo(() => {
    let result = sessions;

    // Free-text: matches reference keywords, session name, group name, and the AI Report's
    // Executive Summary (so e.g. searching "Triethanolamine" or "Darchem" — mentioned in the
    // summary but not the session name/references — still finds the relevant run).
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.group_name?.toLowerCase().includes(q) ||
        (Array.isArray(s.references) && s.references.some(r => r.toLowerCase().includes(q))) ||
        extractExecutiveSummary(s.ai_report).toLowerCase().includes(q)
      );
    }

    if (filterGroup) result = result.filter(s => s.group_name === filterGroup);
    // Work in Progress = not yet accepted; Accepted = every document's review_status is 'accepted'
    // (see is_archived on the session) — either a person clicked Accept All, or the scheduler
    // auto-accepted once AI processing finished (auto_complete schedulers). Not a claim that a
    // human reviewed anything, just that every doc has been marked accepted one way or the other.
    // Archived = the scheduler has finished exporting every document to SharePoint (session.status).
    // These are two independent signals (a session can be Accepted without being Archived yet,
    // or vice versa) — kept as separate filterView values so both can be selected from one Stage dropdown.
    if (filterView === 'rfq') result = result.filter(s => s.stage === 'rfq');
    if (filterView === 'wip') result = result.filter(s => s.stage !== 'rfq' && !s.is_archived);
    if (filterView === 'archive') result = result.filter(s => s.is_archived);
    if (filterView === 'archived') result = result.filter(s => s.status === 'archived');
    if (filterStatus) result = result.filter(s => s.status === filterStatus);

    // Filter by the session's period range overlapping the selected date range
    if (filterDateFrom) result = result.filter(s => !s.period_end || s.period_end >= filterDateFrom);
    if (filterDateTo)   result = result.filter(s => !s.period_start || s.period_start <= filterDateTo);

    // Pending -> Syncing -> Processing -> Complete -> Archived, highest ref number first within
    // each status group.
    result = [...result].sort((a, b) => {
      const statusDiff = (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return refSortValue(b.references) - refSortValue(a.references);
    });

    return result;
  }, [sessions, searchText, filterGroup, filterView, filterStatus, filterDateFrom, filterDateTo]);

  const clearFilters = () => {
    setSearchText('');
    setFilterGroup('');
    setFilterView('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchParams({});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiArchive className="w-7 h-7 text-green-600" />
            Business Intelligence
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {filterGroup
              ? `${filterGroup}${filterView === 'rfq' ? ' — RFQ' : filterView === 'wip' ? ' — Work in Progress' : filterView === 'archive' ? ' — Accepted' : filterView === 'archived' ? ' — Archived' : ''}`
              : 'Business Intelligence documents from SharePoint, Exchange and Doxolve with AI-extracted metadata'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadSessions}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Business Intelligence Run
          </button>
        </div>
      </div>

      {activeCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
          <FiLoader className="w-4 h-4 animate-spin flex-shrink-0" />
          {activeCount} Business Intelligence run{activeCount > 1 ? 's' : ''} in progress. Auto-refreshing every 5 seconds.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Search & Filter bar — only show when there are sessions */}
      {!loading && sessions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          {/* Search row */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={e => { setSearchText(e.target.value); setVisibleCount(PAGE_SIZE); }}
                placeholder="Search by reference, session name or Business Intelligence group…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {searchText && (
                <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <FiX className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(f => !f)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showFilters || (filterGroup || filterStatus || filterDateFrom || filterDateTo) ? 'border-green-500 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              <FiFilter className="w-3.5 h-3.5" />
              Filters
              {(filterGroup || filterView || filterStatus || filterDateFrom || filterDateTo) && (
                <span className="w-2 h-2 rounded-full bg-green-500" />
              )}
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap">
                Clear all
              </button>
            )}
          </div>

          {/* Expanded filter row */}
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t border-gray-100">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Business Intelligence Group</label>
                <select
                  value={filterGroup}
                  onChange={e => { setFilterGroup(e.target.value); setVisibleCount(PAGE_SIZE); }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All groups</option>
                  {groupNames.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Stage</label>
                <select
                  value={filterView}
                  onChange={e => { setFilterView(e.target.value); setVisibleCount(PAGE_SIZE); }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All</option>
                  <option value="rfq">RFQ</option>
                  <option value="wip">Work in Progress</option>
                  <option value="archive">Accepted</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value); setVisibleCount(PAGE_SIZE); }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All statuses</option>
                  <option value="rfq">RFQ</option>
                  <option value="pending">Pending</option>
                  <option value="syncing">Syncing</option>
                  <option value="processing">Processing</option>
                  <option value="complete">Complete</option>
                  <option value="archived">Archived</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Period from</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={e => { setFilterDateFrom(e.target.value); setVisibleCount(PAGE_SIZE); }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Period to</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={e => { setFilterDateTo(e.target.value); setVisibleCount(PAGE_SIZE); }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          )}

          {/* Result count when filtering */}
          {hasActiveFilters && (
            <p className="text-xs text-gray-500">
              Showing {Math.min(filteredSessions.length, visibleCount)} of {filteredSessions.length} matching run{filteredSessions.length !== 1 ? 's' : ''}
              {sessions.length !== filteredSessions.length && ` (${sessions.length} total)`}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <FiLoader className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <FiArchive className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-medium text-gray-700 mb-1">No Business Intelligence runs yet</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
            Create your first Business Intelligence run to sync and categorise documents with AI-extracted metadata.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Start First Business Intelligence Run
          </button>
        </div>
      ) : filteredSessions.length === 0 && hasActiveFilters ? (
        <div className="text-center py-12 text-gray-500">
          <FiSearch className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No runs match your filters</p>
          <button onClick={clearFilters} className="mt-2 text-sm text-green-600 hover:underline">Clear filters</button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.slice(0, visibleCount).map(session => {
            const inProgress = ['syncing', 'processing'].includes(session.status);
            return (
              <div
                key={session.id}
                onClick={() => navigate(`/business-intelligence/${session.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    {session.group_name && (
                      <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-0.5">{session.group_name}</p>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                        {session.name}
                      </h3>
                      <StatusBadge status={session.status} />
                      {session.stage === 'rfq' && <RfqTag />}
                      {session.is_archived && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <FiCheckSquare className="w-3 h-3" />
                          Accepted
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                      {formatPeriod(session.period_start, session.period_end) && (
                        <span className="flex items-center gap-1">
                          <FiCalendar className="w-3.5 h-3.5" />
                          {formatPeriod(session.period_start, session.period_end)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FiFileText className="w-3.5 h-3.5" />
                        {session.doc_count || 0} doc{session.doc_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-gray-400">
                        Created {formatDateTime(session.created_at)}
                      </span>
                    </div>
                    {session.references?.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap mt-1.5">
                        {session.references.map(ref => (
                          <span key={ref} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 font-mono">{ref}</span>
                        ))}
                      </div>
                    )}
                    {extractExecutiveSummary(session.ai_report) && (
                      <p className="mt-1.5 text-sm text-gray-600 line-clamp-2">
                        {extractExecutiveSummary(session.ai_report)}
                      </p>
                    )}
                    {session.error_message && (
                      <p className="mt-1.5 text-xs text-red-600">{session.error_message}</p>
                    )}
                    {inProgress && (
                      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-400 rounded-full animate-pulse" style={{ width: '60%' }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {inProgress && (
                      <button
                        onClick={(e) => handleStop(e, session)}
                        disabled={stopping === session.id}
                        className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all disabled:opacity-30"
                        title="Stop processing"
                      >
                        {stopping === session.id
                          ? <FiLoader className="w-4 h-4 animate-spin" />
                          : <FiStopCircle className="w-4 h-4" />
                        }
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteClick(e, session)}
                      disabled={deleting === session.id}
                      className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30"
                      title="Delete session"
                    >
                      {deleting === session.id
                        ? <FiLoader className="w-4 h-4 animate-spin" />
                        : <FiTrash2 className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {visibleCount < filteredSessions.length && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className="px-5 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
              >
                Load more ({filteredSessions.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <NewRunModal onClose={() => setShowModal(false)} onCreated={handleCreated} defaultGroupId={defaultGroupIdForModal} defaultEntryStage={filterView === 'rfq' ? 'rfq' : 'wip'} />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          session={confirmDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDelete(null)}
          deleting={deleting === confirmDelete.id}
        />
      )}
    </div>
  );
}
